import { Box, Button, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  useResumeAfterVenueMaintenance,
  useVenueMaintenanceState
} from '../../api/useEntry';
import { HEADER_CONTROL_BTN_SX } from './headerButtonSx';

const MAINTENANCE_TRIGGER = 'VENUE_MAINTENANCE';
/** What the window's own panic stamps on both halt keys. */
const PANIC_TRIGGER = 'PANIC';
/** The backend's answer when it could not READ the key — not someone's halt. */
const UNREADABLE = 'unknown';

/**
 * The operator's view of a venue maintenance halt, and the only button that
 * lifts it.
 *
 * 🚨 **Lifting the halt is not resuming trading, and the copy here must never
 * say otherwise.** A maintenance window fires the operator PANIC, so the rules
 * are paused with their maker flags down and every opener group is parked
 * `podsDesired=off`. This button removes the placement gate over a platform
 * that is already disarmed: what comes back is nothing, until the operator
 * STARTs the groups and re-arms the rules deliberately.
 *
 * That is true only of a halt this watcher armed under the CURRENT behaviour.
 * Before 2026-08-22 the window halted and cancelled and touched nothing else,
 * so the key alone was the only thing standing between an armed rule set and
 * the book. An ORPHANED key — one with no window tracked — can be exactly that,
 * or one left over after the operator already re-armed, which is why its copy
 * promises nothing about the rules and tells the operator to go look.
 *
 * The watcher currently ships OBSERVE-ONLY, so the common case here is a window
 * with NOTHING halted: the chip says so and there is no button, because the
 * response is the operator's to choose. The halted copy below is what the same
 * component renders once `VENUE_MAINTENANCE_ENFORCE` is on.
 *
 * The watcher halts BOTH engines ahead of an announced Kalshi/Polymarket
 * maintenance window and — deliberately — never lifts the halt on its own. So
 * there has to be a control here: the only other thing that clears the halt key
 * is global START OPENER, which the UI greys out whenever global state already
 * reads "Running". That is the 2026-08-06 deadlock, and it applies to EVERY
 * branch of this control, the panic path included: `deriveOpenerPower` reads
 * `booted` + `nodeReady`/`nodeServing` only, and the maintenance panic parks
 * the group ENGINE pods without touching the orchestrator — so the header still
 * reads "Running" and still offers Stop, not Start.
 *
 * With ONE exception, and it is worth knowing: a `PANIC` key standing with no
 * window tracked (the hold lost, e.g. after a long outage plus a restart) is not
 * attributable to this watcher, so no button is offered and the backend would
 * refuse it anyway. The exit there is `POST /api/system/opener/start` per
 * direction; the key's own ~1h TTL also expires it, over a platform whose rules
 * are paused and whose group pods are parked.
 *
 * EVERYTHING it renders is derived from the polled backend state — never from
 * the resume mutation's own result. This component is mounted on two SEPARATE
 * routes (the opener page and the unwinder page), and the backend drops its
 * held window on every resume, so a verdict cached in the browser disappears
 * the moment the operator navigates: they would resume from the opener page,
 * see "still armed", switch to the unwinder page to check the other direction,
 * and find nothing at all — a false all-clear on the page for the direction
 * most likely still stopped. Reading the halt keys through also means the
 * warning clears itself when someone lifts the other halt, instead of sticking
 * until the next maintenance window.
 */
export const VenueMaintenanceControl = () => {
  const { data } = useVenueMaintenanceState();
  const resume = useResumeAfterVenueMaintenance();

  const held = data?.held ?? null;
  const halts = data?.halts;
  const armFailures = data?.haltArmFailures ?? [];

  const armed = (
    [
      ['opener', halts?.opener],
      ['unwinder', halts?.unwinder]
    ] as const
  ).filter(([, trigger]) => Boolean(trigger));

  // The watcher's observe-only posture: it tracked the window and armed
  // nothing. Rendering the halted copy here would tell the operator the platform
  // is stopped while both engines keep placing into the outage. `undefined` (a
  // backend older than the field) keeps the enforcing reading, which is what it
  // always did.
  const observeOnly = held?.enforced === false;
  // "The watcher did not arm" and "nothing is armed" are DIFFERENT claims, and
  // only the second one may hide the resume. A read that failed (`unknown`) or a
  // key left over from an enforcing run both show up here while `enforced` is
  // false — and hiding the button there recreates the 2026-08-06 deadlock this
  // component exists to break: the engines are stopped, the chips say so, and
  // the only control that lifts them is gone. Pressing it when nothing is ours
  // is harmless — `clearIfTrigger` only ever removes our own trigger.
  const nothingArmed = armed.length === 0;

  // A halt standing on a key that is NOT ours: an operator PANIC or STOP took
  // it over, so pressing the halt-lift here will not (and must not) lift it.
  // `unknown` is excluded on purpose — it means the backend could not READ the
  // key, and telling the operator to go clear a stop that does not exist is its
  // own kind of wrong answer.
  //
  // And `PANIC` is NOT foreign while this window's own panic is what wrote it:
  // the window fires the operator PANIC, so after every enforced window BOTH
  // keys read `PANIC` by design. Calling that someone else's stop would put
  // "clear that panic to let the opener trade again" on screen about the very
  // window the operator is looking at, next to a button that does clear it.
  const ourPanic = held?.panicFired === true;
  // `panicFired` says the panic RAN; this says it left the rules DOWN. Only the
  // second supports "lifting this restarts nothing": the panic pauses each
  // direction's rules separately and swallows a Mongo failure, so it can report
  // on both while one direction is still fully armed — and that direction's
  // makers go straight back on the book when the gate lifts.
  //
  // `=== true`, never `!== false`. The field is absent on a backend older than
  // it, and the FE ships through CloudFront independently of EKS — so UNKNOWN is
  // reachable by deploy order alone, and reading it as "disarmed" restores the
  // strong claim with nothing behind it. Fail closed: unknown takes the "does
  // not know whether the rules are armed" posture.
  const panicDisarmed = ourPanic && held?.panicDisarmed === true;
  // THREE states, like the backend's cards. Collapsing "the panic definitely
  // never ran" into "unknown or partial" sent the operator to read a
  // maintenance-panic card that was never emitted — on the branch where the
  // rules are CERTAINLY armed and lifting the gate re-posts everything.
  // `!observeOnly`, not `enforced === true`: a payload without `enforced` reads
  // as enforcing everywhere else in this file, and requiring the explicit true
  // silently dropped the third state on exactly those payloads.
  // …and only while the standing keys are OURS. With a human's PANIC or STOP on
  // them, the rules WERE paused and the halt-lift will not touch that key — so
  // the strong claim ("nothing paused the rules, lifting puts EVERY maker back")
  // would contradict the chip beside it and mis-state what the button does.
  const noPanicRan =
    !observeOnly &&
    held?.panicFired === false &&
    armed.every(([, trigger]) => trigger === MAINTENANCE_TRIGGER);
  const foreign = armed.filter(
    ([, trigger]) =>
      trigger !== MAINTENANCE_TRIGGER &&
      trigger !== UNREADABLE &&
      !(ourPanic && trigger === PANIC_TRIGGER)
  );
  // Attributed to this window, but still worth NAMING. `ourPanic` is one
  // window-level boolean over both directions and any key reading `PANIC`, so a
  // real operator PANIC pressed mid-window lands in here too — and suppressing
  // the chip entirely would let them lift, from this bar, the halt their own
  // deliberate panic armed, with nothing on screen having mentioned it.
  const ourPanicKeys = ourPanic
    ? armed.filter(([, trigger]) => trigger === PANIC_TRIGGER)
    : [];
  const unreadable = armed.filter(([, trigger]) => trigger === UNREADABLE);
  // A maintenance key standing that the headline chip does NOT account for.
  // Two ways to get here and both need naming, because in both the engine is
  // stopped and nothing else on this bar would say so:
  //   - no window in memory — a halt that survived a restart whose adoption
  //     failed, or one orphaned by a race. Nobody refreshes it, nobody else
  //     clears it.
  //   - observe-only — the watcher armed nothing for this window, so the
  //     headline says exactly that, and a key from an earlier enforcing run
  //     would otherwise be invisible: not `foreign` (the trigger IS ours) and
  //     not orphaned (there IS a window). The chip that says "read the ones
  //     beside me" has to have something to point at.
  // Per DIRECTION, not globally: the two keys live on different cluster slots,
  // so one read can fail while the other answers.
  const unaccountedMaintenance =
    held && !observeOnly
      ? []
      : armed.filter(([, t]) => t === MAINTENANCE_TRIGGER);
  // An UNREADABLE key counts for the BUTTON: it may well be ours, and the
  // resume is harmless if it is not.
  const orphaned =
    !held &&
    armed.some(([, t]) => t === MAINTENANCE_TRIGGER || t === UNREADABLE);

  // `isError` is part of the gate: a resume that never reached the manager has
  // to be visible even when the poll says everything is clear, or a failed
  // request looks exactly like a successful one.
  if (
    !held &&
    !orphaned &&
    foreign.length === 0 &&
    unreadable.length === 0 &&
    armFailures.length === 0 &&
    !resume.isError
  ) {
    return null;
  }

  const waiting = Boolean(data?.awaitingOperator);
  // Only meaningful when the VENUE gave us the end. For an unannounced outage
  // the backend invents a placeholder it explicitly refuses to hold to, and
  // printing it would be a fabricated schedule.
  const scheduleKnown = Boolean(held?.endsAtKnown);
  const windowEnded = Boolean(
    held && scheduleKnown && Date.now() >= held.endsAt
  );
  const minutesLeft =
    held && scheduleKnown
      ? Math.max(0, Math.round((held.endsAt - Date.now()) / 60_000))
      : 0;

  const chipLabel = () => {
    if (!held) return '';
    // "NOT halted" is only sayable when nothing is armed. With a key standing
    // (ours from an earlier enforcing run, or an unreadable one) the honest
    // claim is narrower: the WATCHER armed nothing. What IS armed gets its own
    // chip right beside this one.
    if (observeOnly) {
      return nothingArmed
        ? `${held.venue} maintenance — NOT halted`
        : `${held.venue} maintenance — watcher armed nothing`;
    }
    if (waiting) {
      return nothingArmed
        ? `${held.venue} maintenance over — nothing armed`
        : `${held.venue} maintenance over — halted`;
    }
    if (nothingArmed) return `${held.venue} maintenance — nothing armed`;
    if (!scheduleKnown) return `${held.venue} outage — halted, no end announced`;
    if (windowEnded) return `${held.venue} maintenance — halted, venue unconfirmed`;
    return `${held.venue} maintenance — halted${minutesLeft > 0 ? ` ~${minutesLeft}min` : ''}`;
  };

  const chipTitle = () => {
    if (!held) return '';
    // Before every posture branch: with no key standing there is no halt to
    // describe, whatever the watcher thinks it is holding. This is the window
    // between an operator clearing the keys by hand and the watcher's next pass
    // noticing — the label already says it, and the tooltip used to contradict
    // it in the same chip.
    if (!observeOnly && nothingArmed) {
      return `${held.venue} maintenance ("${held.title}") is tracked, but NO halt key is standing — nothing is gating placement on either direction right now. If you cleared them, the watcher drops this hold on its next pass.`;
    }
    if (observeOnly) {
      return nothingArmed
        ? `${held.venue} is in maintenance ("${held.title}") and the watcher is OBSERVE-ONLY: nothing was halted and no order was cancelled, so both engines are still placing into it. Stopping them is your call.`
        : `${held.venue} is in maintenance ("${held.title}") and the watcher armed nothing for it — but a halt key IS standing. Read the chips beside this one for what is actually armed; this button clears the key if it is ours.`;
    }
    // ABOVE the posture split, not inside `waiting`. `panicFired: false` is true
    // from the moment the window opens, and mid-window is where the operator
    // sits for the whole of it — those branches were hedging ("may put a side's
    // makers back") and pointing at a maintenance-panic card that, with no panic,
    // was never emitted.
    if (noPanicRan) {
      return `${held.venue} maintenance ("${held.title}") is tracked and the engines are halted, but NO panic ran for this window: nothing paused the rules, so lifting the halt puts EVERY maker on BOTH directions straight back. There is no maintenance-panic card to read — stop them by hand first.`;
    }
    if (waiting) {
      return panicDisarmed
        ? `${held.venue} maintenance ("${held.title}") is OVER and the venue reports healthy. This window FIRED the operator PANIC — halt, rules paused, group pods parked — so lifting the halt restarts nothing by itself: coming back is starting each group's pods again (Pairs Bucket → group → pods) and re-arming its rules. What the panic ACHIEVED is on its own maintenance-panic card.`
        : `${held.venue} maintenance ("${held.title}") is OVER and the venue reports healthy, and the engines are still halted. This window is NOT recorded as having disarmed both directions, so this control does NOT know whether the rules are armed — the maintenance-panic card says which side, if any, was left armed.`;
    }
    if (!scheduleKnown) {
      return panicDisarmed
        ? `${held.venue} is refusing orders and published no end time, so there is no schedule to show. The halt stands until YOU lift it — the venue reporting healthy only ends the wait, it never lifts anything. What this window's PANIC achieved is on the maintenance-panic card.`
        : `${held.venue} is refusing orders and published no end time, so there is no schedule to show. The halt stands until YOU lift it, and this window is NOT recorded as having disarmed both directions — check whether the rules are armed before you do.`;
    }
    if (windowEnded) {
      return panicDisarmed
        ? `${held.venue} maintenance ("${held.title}") is past its scheduled end, but the venue has not confirmed it is healthy again. Lifting the halt now is your call — this window's panic paused the rules on both directions, so it resumes nothing by itself.`
        : `${held.venue} maintenance ("${held.title}") is past its scheduled end, but the venue has not confirmed it is healthy again, and this window is NOT recorded as having disarmed both directions. Read the maintenance-panic card: lifting the halt may put a side's makers straight back.`;
    }
    return `${held.venue} maintenance in progress ("${held.title}") until ${new Date(held.endsAt).toLocaleTimeString()}.`;
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {held && (
          <Tooltip title={chipTitle()} placement="top">
            <Chip
              size="small"
              icon={<BuildCircleIcon sx={{ fontSize: 14 }} />}
              color={waiting || observeOnly ? 'warning' : 'error'}
              variant={waiting || observeOnly ? 'outlined' : 'filled'}
              label={chipLabel()}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
        {unreadable.map(([direction]) => (
          <Tooltip
            key={`unreadable-${direction}`}
            title={`The ${direction} halt key could not be READ, so its state is UNKNOWN — assume the ${direction} is stopped until this clears. It is not evidence that anyone halted it.`}
            placement="top"
          >
            <Chip
              size="small"
              color="warning"
              label={`${direction} halt state UNREADABLE`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {unaccountedMaintenance.map(([direction]) => (
          <Tooltip
            key={`orphan-${direction}`}
            title={
              held
                ? `A maintenance halt is armed on the ${direction} even though the watcher armed nothing for this window — most likely it is left over from before the kill switch was flipped. That engine is STOPPED. This button clears the key.`
                : `A maintenance halt is still armed on the ${direction} but the watcher has no window for it — most likely it outlived a restart. That engine is STOPPED. This button clears the key.`
            }
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              icon={<BuildCircleIcon sx={{ fontSize: 14 }} />}
              label={
                held
                  ? `${direction} halt armed — maintenance`
                  : `${direction} halt armed — no window tracked`
              }
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {((held && !nothingArmed) || orphaned) && (
          // Offered even mid-window: the operator overrides the watcher, not the
          // other way round. The tooltip says plainly what they are overriding.
          // NOT offered with nothing armed, whatever the posture. There is no
          // halt to lift, and a button claiming to lift one is how an operator
          // ends up believing the platform was ever stopped — which is also the
          // window between the operator clearing the keys by hand and the
          // watcher's next pass noticing and dropping the hold.
          <Tooltip
            title={
              orphaned
                ? 'Clear the orphaned maintenance halt key. There is no window tracked for it, so this control does NOT know whether the rules are armed or the groups are up — check them before you press: if they are, lifting the gate puts the makers straight back.'
                : observeOnly
                  ? 'The watcher halted nothing for this window, but something IS armed on a halt key. This clears it if it is the maintenance watcher\'s — and since no panic ran here, the rules may well be armed: lifting the gate can put their makers straight back.'
                  : noPanicRan
                    ? 'Clear the maintenance halt key. NO panic ran for this window, so nothing paused the rules: lifting this gate puts EVERY maker on BOTH directions straight back on the book. Stop them by hand first — there is no maintenance-panic card for this one.'
                    : waiting && panicDisarmed
                    ? 'Clear the maintenance halt key. This window FIRED the operator PANIC — halt, rules paused, group pods parked — so this does NOT resume trading; what the panic actually ACHIEVED is on the maintenance-panic card, and it is worth reading before you press. Coming back is starting each group\'s pods again and re-arming its rules.'
                    : waiting
                      ? 'Clear the maintenance halt key. This window is NOT recorded as having disarmed both directions — the panic may not have run, or one side\'s rule pause may have failed — so this control does NOT know whether the rules are armed. Read the maintenance-panic card before you press: if they are, lifting the gate puts their makers straight back.'
                      : 'The venue has NOT confirmed it is trading again, and this removes the gate before it is known good. Read the maintenance-panic card first: if it could not confirm the rules are paused, lifting the halt puts their makers straight back.'
            }
            placement="top"
          >
            <span>
              <Button
                // Never `success`, and never a ▶: this button opens a gate, it
                // does not start anything. A green GO next to copy that says
                // "resumes nothing" is read as the copy being cautious.
                variant={waiting || orphaned ? 'contained' : 'outlined'}
                color="warning"
                size="small"
                startIcon={
                  resume.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <LockOpenIcon />
                  )
                }
                onClick={() => resume.mutate()}
                disabled={resume.isLoading}
                sx={HEADER_CONTROL_BTN_SX}
              >
                {waiting || orphaned ? 'Lift halt' : 'Lift halt anyway'}
              </Button>
            </span>
          </Tooltip>
        )}
        {/* A halt on a key that is not ours. Named by its actual trigger and by
            direction: "PANIC" sends the operator to the panic lockout, "STOP"
            does not, and one direction being held while the other trades is a
            different situation from both being down. */}
        {ourPanicKeys.map(([direction]) => (
          <Tooltip
            key={`ourpanic-${direction}`}
            title={`The ${direction} halt key reads PANIC — this window fired one, and the halt-lift above clears it. If YOU pressed panic during this window, that is the same key: lifting it here removes the gate your panic armed. What either panic left behind is on the maintenance-panic card.`}
            placement="top"
          >
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${direction} PANIC key — this window's`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {foreign.map(([direction, trigger]) => (
          <Tooltip
            key={direction}
            title={
              trigger === 'PANIC'
                ? `The ${direction} halt key is held by a PANIC, not by the maintenance watcher — this button will NOT lift it. Clear that panic, then start the groups' pods again and re-arm their rules.`
                : `The ${direction} halt key is held by a ${trigger}, not by the maintenance watcher — this button will NOT lift it. A STOP also pauses the rules and parks the group pods, AND arms a 24h lockout that refuses START: clear the lockout first, then start the groups' pods and re-arm their rules.`
            }
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              label={`${direction} halted by ${trigger}`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {/* The loudest thing this control can say: a direction the watcher could
            NOT halt may still be placing INTO the window. */}
        {armFailures.length > 0 && (
          <Tooltip
            title={`The halt could not be armed on: ${armFailures.join('; ')}. That engine may still be placing into the maintenance window — check it by hand.`}
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              label="halt NOT armed on one direction"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
        {resume.isError && (
          <Tooltip
            // Deliberately does NOT claim the halt is still armed: the keys are
            // cleared BEFORE the steps that can throw, so a 500 here is
            // consistent with the gate being gone. The chips above say what is
            // actually armed; this one only says the request failed.
            title="The halt-lift request failed. Check the halt chips above for what is actually armed before assuming anything changed."
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="halt-lift request FAILED"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
